/**
 * Tawk's widget posts performance metrics to `va.tawk.to/log-performance/*`.
 * That endpoint does not respond to browser CORS preflights from app origins,
 * which spams the console without affecting chat. We short-circuit those calls
 * until Tawk fixes ACAO on their side.
 */
const TAWK_VA_HOST = "va.tawk.to";
const TAWK_PERF_PATH = "log-performance";

const isTawkPerformanceTelemetryUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url, window.location.origin);
    return (
      parsed.hostname === TAWK_VA_HOST ||
      parsed.hostname.endsWith(`.${TAWK_VA_HOST}`)
    ) && parsed.pathname.includes(TAWK_PERF_PATH);
  } catch {
    return (
      url.includes(TAWK_VA_HOST) && url.includes(TAWK_PERF_PATH)
    );
  }
};

let guardInstalled = false;

export const installTawkTelemetryGuard = (): void => {
  if (typeof window === "undefined" || guardInstalled) {
    return;
  }
  guardInstalled = true;

  const noopResponse = () =>
    Promise.resolve(new Response(null, { status: 204, statusText: "No Content" }));

  const originalFetch = window.fetch.bind(window);
  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    const url =
      typeof input === "string"
        ? input
        : input instanceof Request
          ? input.url
          : String(input);
    if (isTawkPerformanceTelemetryUrl(url)) {
      return noopResponse();
    }
    return originalFetch(input, init);
  }) as typeof window.fetch;

  if (typeof navigator.sendBeacon === "function") {
    const originalSendBeacon = navigator.sendBeacon.bind(navigator);
    navigator.sendBeacon = (url: string | URL, data?: BodyInit | null) => {
      if (isTawkPerformanceTelemetryUrl(String(url))) {
        return true;
      }
      return originalSendBeacon(url, data);
    };
  }

  const OriginalXHR = window.XMLHttpRequest;
  class TawkTelemetrySafeXHR extends OriginalXHR {
    private requestUrl = "";

    open(
      method: string,
      url: string | URL,
      async = true,
      username?: string | null,
      password?: string | null,
    ): void {
      this.requestUrl = String(url);
      super.open(method, url, async, username, password);
    }

    send(body?: Document | XMLHttpRequestBodyInit | null): void {
      if (isTawkPerformanceTelemetryUrl(this.requestUrl)) {
        queueMicrotask(() => {
          Object.defineProperty(this, "readyState", { value: 4, configurable: true });
          Object.defineProperty(this, "status", { value: 204, configurable: true });
          this.dispatchEvent(new Event("readystatechange"));
          this.dispatchEvent(new Event("load"));
          this.dispatchEvent(new Event("loadend"));
        });
        return;
      }
      super.send(body);
    }
  }

  window.XMLHttpRequest = TawkTelemetrySafeXHR as typeof XMLHttpRequest;
};
